import { Button, cn, Modal } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { ClockCountdown as ClockCountdownIcon } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { formatDay } from "@/lib/utils/date";
import { openBordereauPdf, openFichier } from "./open-pieces";
import { SYNTHESE_LABELS, VISA_EN_RETARD_LABEL } from "./synthese-cell";

type Rapport = {
  numero: string | null;
  date: string | null;
  /** Null when the user cannot open the report. */
  fichierId: number | null;
  avis: Array<{
    nAvis: string | null;
    statusAvis: string | null;
    observations: Array<{
      sujet: string | null;
      description: string;
      consignes: string[];
    }>;
  }>;
};

export type SyntheseAvisData = {
  documentName: string;
  /** Un avis requis est en attente avec délai dépassé. */
  visaEnRetard: boolean;
  /** Équipes dont l'avis requis est en retard. */
  lateTeamIds?: number[];
  syntheseAvis: "obs" | "ok" | "attente" | null;
  notices: Array<{
    teamId: number;
    companyName: string;
    intervenantConcerne: string;
    avisRequis: boolean;
    bordereaux: Array<{
      numero: string | null;
      date: string | null;
      /** Null when the user cannot open the bordereau. */
      slipId: number | null;
    }>;
    rapports: Rapport[];
  }>;
};

export type SyntheseAvisPopupProps = {
  open: boolean;
  onClose: () => void;
  data: SyntheseAvisData | null;
  yardId: string | undefined;
};

const statusLabels: Record<string, string> = {
  favorable: "Favorable",
  defavorable: "Défavorable",
  vu_avec_observation: "Vu avec obs.",
  vu_sans_observation: "Vu sans obs.",
};

const statusColors: Record<string, string> = {
  favorable: "text-green-600",
  defavorable: "text-red-600",
  vu_avec_observation: "text-amber-600",
  vu_sans_observation: "text-green-600",
};

const headCellClass =
  "px-3 py-2 text-left font-medium text-zinc-700 dark:text-zinc-300";
const cellClass = "px-3 py-2 align-top text-zinc-600 dark:text-zinc-400";
const linkClass = "text-blue-600 hover:underline dark:text-blue-400";

function DateCell({ value }: { value: string | null }) {
  return <>{isNotNil(value) ? formatDay(value) : "-"}</>;
}

function RapportCells({
  rapport,
  yardId,
}: {
  rapport: Rapport;
  yardId: string | undefined;
}) {
  const { fichierId } = rapport;
  return (
    <>
      <td className={cellClass}>
        {rapport.avis.map((avis, index) => (
          <div
            key={`${avis.nAvis}-${index}`}
            className={cn(
              "font-medium",
              (isNotNil(avis.statusAvis)
                ? statusColors[avis.statusAvis]
                : undefined) ?? "text-zinc-600",
            )}
          >
            {isNotNil(avis.nAvis) && `${avis.nAvis} / `}
            {isNotNil(avis.statusAvis)
              ? (statusLabels[avis.statusAvis] ?? avis.statusAvis)
              : "-"}
          </div>
        ))}
      </td>
      <td className={cellClass}>
        <ul className="space-y-1">
          {rapport.avis
            .flatMap((avis) => avis.observations)
            .map((observation, index) => (
              <li key={`${observation.description}-${index}`}>
                {isNotNil(observation.sujet) && (
                  <span className="font-medium">{observation.sujet} : </span>
                )}
                {observation.description}
                {observation.consignes.map((consigne) => (
                  <p
                    key={consigne}
                    className="text-xs text-zinc-500 dark:text-zinc-400"
                  >
                    Consigne : {consigne}
                  </p>
                ))}
              </li>
            ))}
        </ul>
      </td>
      <td className={cellClass}>
        {isNil(fichierId) || isNil(yardId) ? (
          (rapport.numero ?? "-")
        ) : (
          <button
            type="button"
            onClick={() => openFichier(yardId, fichierId)}
            className={linkClass}
          >
            {rapport.numero ?? "PDF"}
          </button>
        )}
      </td>
      <td className={cn(cellClass, "whitespace-nowrap")}>
        <DateCell value={rapport.date} />
      </td>
    </>
  );
}

export function SyntheseAvisPopup({
  open,
  onClose,
  data,
  yardId,
}: SyntheseAvisPopupProps) {
  if (!data) return null;

  const recipientRows = (
    notice: SyntheseAvisData["notices"][number],
  ): ReactNode => {
    const rowSpan = Math.max(notice.rapports.length, 1);
    const late = data.lateTeamIds?.includes(notice.teamId) ?? false;
    const recipientCells = (
      <>
        <td
          rowSpan={rowSpan}
          className={cn(cellClass, "text-zinc-800 dark:text-zinc-200")}
        >
          {notice.companyName}
        </td>
        <td rowSpan={rowSpan} className={cellClass}>
          {notice.intervenantConcerne}
        </td>
        <td rowSpan={rowSpan} className={cn(cellClass, "text-center")}>
          {notice.avisRequis ? (
            <span className="text-blue-600">Oui</span>
          ) : (
            <span className="text-zinc-400">Non</span>
          )}
        </td>
        <td rowSpan={rowSpan} className={cellClass}>
          {notice.bordereaux.map(({ numero, slipId }, index) => (
            <div key={`${numero}-${index}`}>
              {isNil(slipId) || isNil(yardId) ? (
                (numero ?? "-")
              ) : (
                <button
                  type="button"
                  onClick={() =>
                    openBordereauPdf(yardId, slipId, notice.teamId)
                  }
                  className={linkClass}
                >
                  {numero ?? "PDF"}
                </button>
              )}
            </div>
          ))}
        </td>
        <td
          rowSpan={rowSpan}
          className={cn(
            cellClass,
            "whitespace-nowrap border-r border-zinc-300",
          )}
        >
          {notice.bordereaux.map(({ numero, date }, index) => (
            <div key={`${numero}-${index}`}>
              <DateCell value={date} />
            </div>
          ))}
        </td>
      </>
    );

    if (notice.rapports.length === 0) {
      return (
        <tr
          key={notice.teamId}
          className="border-b border-zinc-100 dark:border-zinc-700/50"
        >
          {recipientCells}
          <td colSpan={4} className={cn(cellClass, "text-center italic")}>
            <span className="inline-flex items-center gap-1.5">
              {late && (
                <ClockCountdownIcon
                  size={16}
                  weight="fill"
                  className="text-red-600 dark:text-red-400"
                  aria-label={VISA_EN_RETARD_LABEL}
                >
                  <title>{VISA_EN_RETARD_LABEL}</title>
                </ClockCountdownIcon>
              )}
              Aucun retour
            </span>
          </td>
        </tr>
      );
    }
    return notice.rapports.map((rapport, index) => (
      <tr
        key={`${notice.teamId}-${rapport.numero}`}
        className="border-b border-zinc-100 dark:border-zinc-700/50"
      >
        {index === 0 && recipientCells}
        <RapportCells rapport={rapport} yardId={yardId} />
      </tr>
    ));
  };

  return (
    <Modal.Root open={open} onOpenChange={(e) => !e.open && onClose()}>
      <Modal.Backdrop />
      <Modal.Positioner>
        <Modal.Content size="full">
          <Modal.Header>
            <Modal.Title>Synthèse des avis</Modal.Title>
          </Modal.Header>

          <div className="px-6 pb-6">
            <div className="mb-4">
              <p className="text-sm text-zinc-600 dark:text-zinc-400">
                {data.documentName}
              </p>
              <span className="mt-1 inline-flex items-center gap-1.5">
                {data.syntheseAvis && (
                  <span className="inline-block rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-medium dark:bg-zinc-800">
                    {SYNTHESE_LABELS[data.syntheseAvis]}
                  </span>
                )}
                {/* Même picto "visa en retard" que sur la liste */}
                {data.visaEnRetard && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 dark:text-red-400">
                    <ClockCountdownIcon size={16} weight="fill" />
                    {VISA_EN_RETARD_LABEL}
                  </span>
                )}
              </span>
            </div>

            {data.notices.length === 0 ? (
              <p className="text-sm text-zinc-500">
                Aucun destinataire pour avis.
              </p>
            ) : (
              <div className="max-h-[60vh] overflow-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-zinc-200 dark:border-zinc-700">
                      <th className={headCellClass}>Société destinataire</th>
                      <th className={headCellClass}>Intervenant concerné</th>
                      <th className={cn(headCellClass, "text-center")}>
                        Avis requis
                      </th>
                      <th className={headCellClass}>Bord. N°</th>
                      <th className={headCellClass}>Bord. date</th>
                      <th className={headCellClass}>Avis</th>
                      <th className={headCellClass}>Observations</th>
                      <th className={headCellClass}>Rapport examen</th>
                      <th className={headCellClass}>Date rapport</th>
                    </tr>
                  </thead>
                  <tbody>{data.notices.map(recipientRows)}</tbody>
                </table>
              </div>
            )}
          </div>
          <Modal.Footer>
            <Button onClick={onClose}>Fermer</Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal.Positioner>
    </Modal.Root>
  );
}
